import { FC } from 'react';
import { css } from '@emotion/react';
import { spacing, colors } from '../../../styles';
import { ToolRendererProps } from './types';

interface SearchResult {
  query: string;
  results_count?: number;
  articles?: Array<{
    title: string;
    link: string;
    snippet?: string;
    date?: string;
  }>;
  status?: string;
  error?: string;
}

const SingleSearchResult: FC<{ result: SearchResult; showDivider?: boolean }> = ({ result, showDivider }) => {
  if (result.status === 'error') {
    return (
      <div
        css={css`
          margin-bottom: ${spacing.deci};
          ${showDivider &&
          `
            padding-top: ${spacing.deci};
            border-top: 1px solid ${colors.cloud};
            html[data-theme='dark'] & {
              border-top-color: ${colors.tungsten};
            }
          `}
        `}
      >
        <div
          css={css`
            font-size: 12px;
            color: ${colors.silver};
            margin-bottom: ${spacing.centi};
          `}
        >
          Search for &ldquo;{result.query}&rdquo;
        </div>
        <div
          css={css`
            font-size: 14px;
            color: ${colors.berry};
            html[data-theme='dark'] & {
              color: ${colors.berry};
            }
          `}
        >
          Error: {result.error}
        </div>
      </div>
    );
  }

  if (!result.articles || !Array.isArray(result.articles)) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
          ${showDivider &&
          `
            padding-top: ${spacing.deci};
            border-top: 1px solid ${colors.cloud};
            html[data-theme='dark'] & {
              border-top-color: ${colors.tungsten};
            }
          `}
        `}
      >
        No results for &ldquo;{result.query}&rdquo;
      </div>
    );
  }

  return (
    <div
      css={css`
        ${showDivider &&
        `
          padding-top: ${spacing.deci};
          border-top: 1px solid ${colors.cloud};
          html[data-theme='dark'] & {
            border-top-color: ${colors.tungsten};
          }
        `}
      `}
    >
      <div
        css={css`
          font-size: 12px;
          color: ${colors.silver};
          margin-bottom: ${spacing.centi};
        `}
      >
        Found {result.results_count || result.articles.length} results for &ldquo;{result.query}
        &rdquo;
      </div>
      {result.articles.map((article, index) => (
        <div
          key={index}
          css={css`
            margin-bottom: ${spacing.deci};
            line-height: 1.4;
          `}
        >
          <div
            css={css`
              font-size: 14px;
              margin-bottom: ${spacing.micro};
            `}
          >
            <a
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              css={css`
                color: ${colors.petrolBlue};
                text-decoration: none;

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              {article.title}
            </a>
          </div>
          {article.snippet && (
            <div
              css={css`
                font-size: 13px;
                color: ${colors.liquorice};
                margin-bottom: ${spacing.micro};

                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {article.snippet}
            </div>
          )}
          {article.date && (
            <div
              css={css`
                font-size: 12px;
                color: ${colors.silver};
              `}
            >
              {article.date}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const WebSearchRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Searching web...
      </div>
    );
  }

  // Handle batch format: { searches: [...] }
  const searches: SearchResult[] = output.searches || [output];

  if (!searches.length) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Web search completed
      </div>
    );
  }

  return (
    <div
      css={css`
        margin: ${spacing.centi} 0 0 0;
      `}
    >
      {searches.map((searchResult, index) => (
        <SingleSearchResult key={index} result={searchResult} showDivider={index > 0} />
      ))}
    </div>
  );
};
